<template>
  <div>
    <v-container fluid>
      <base-material-card
        icon="mdi-shield-crown"
        title="ผู้ดูแลระบบ: รายงานผลการประเมินการปฏิบัติงาน พนักงานราชการ (แบบ ปผ.พนร.)"
        class="px-5 py-3"
      >
        <!-- ===== ส่วนหัว: ตัวกรองข้อมูล ===== -->
        <v-row class="mb-2">
          <v-col cols="12">
            <v-alert
              type="info"
              prominent
              outlined
              border="left"
              colored-border
              elevation="2"
              icon="mdi-shield-check"
              class="mb-0"
            >
              <div class="text-subtitle-1 font-weight-bold">
                ระบบติดตามผลการประเมินการปฏิบัติงาน
                และรายงานข้อมูลการลา/มาสาย/ขาดราชการ (Admin)
              </div>
              <div class="text-body-2">
                ตรวจสอบ อนุมัติ และจัดการผลการประเมินของสถานศึกษาทุกแห่ง
                ตามประกาศ สอศ. พ.ศ. 2569
              </div>
              <div class="text-caption mt-1 grey--text">
                <v-icon x-small>mdi-scale-balance</v-icon>
                เกณฑ์คะแนน: ดีเด่น 95-100 | ดีมาก 85-94 | ดี 75-84 | พอใช้ 65-74
                | ต้องปรับปรุง &lt;65
              </div>
            </v-alert>
          </v-col>
        </v-row>

        <v-row class="mb-4">
          <v-col cols="12" md="3">
            <v-select
              v-model="selectedYear"
              :items="years"
              label="ปีงบประมาณ"
              outlined
              dense
              prepend-inner-icon="mdi-calendar-range"
              @change="loadAllData"
            ></v-select>
          </v-col>

          <v-col cols="12" md="3">
            <v-select
              v-model="selectedRound"
              :items="roundOptions"
              label="รอบการประเมิน"
              outlined
              dense
              prepend-inner-icon="mdi-timer-sand"
              @change="loadAllData"
            ></v-select>
          </v-col>

          <v-col cols="12" md="3">
            <v-autocomplete
              v-model="selectedCollege"
              :items="collegeList"
              item-text="organization_name"
              item-value="user_code"
              label="สถานศึกษา"
              outlined
              dense
              clearable
              prepend-inner-icon="mdi-school"
              @change="loadAllData"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{
                    item.organization_name
                  }}</v-list-item-title>
                  <v-list-item-subtitle class="text-caption"
                    >รหัส: {{ item.user_code }}</v-list-item-subtitle
                  >
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="12" md="3" class="text-right">
            <v-btn
              color="primary"
              @click="loadAllData"
              :loading="loading"
              class="mr-2"
            >
              <v-icon left>mdi-refresh</v-icon>
              โหลดใหม่
            </v-btn>
            <v-btn
              color="success"
              @click="exportAdminReport"
              :disabled="allAssessments.length === 0"
            >
              <v-icon left>mdi-file-excel</v-icon>
              ส่งออก Excel
            </v-btn>
          </v-col>
        </v-row>

        <!-- ===== Dashboard สถิติรวม ===== -->
        <v-row class="mb-4">
          <!-- แถวที่ 1: สถิติหลัก -->
          <v-col cols="12" sm="6" md="3">
            <v-card class="pa-3 text-center stat-card" outlined>
              <v-icon large color="blue" class="mb-1">mdi-account-group</v-icon>
              <h2 class="blue--text mb-1">{{ stats.totalEmployees }}</h2>
              <p class="text-body-2 grey--text mb-0">พนักงานทั้งหมด</p>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card class="pa-3 text-center stat-card" outlined>
              <v-icon large color="green" class="mb-1"
                >mdi-clipboard-check</v-icon
              >
              <h2 class="green--text mb-1">{{ stats.totalAssessments }}</h2>
              <p class="text-body-2 grey--text mb-0">รายการประเมินทั้งหมด</p>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card class="pa-3 text-center stat-card" outlined>
              <v-icon large color="orange" class="mb-1">mdi-clock-check</v-icon>
              <h2 class="orange--text mb-1">{{ stats.pendingApproval }}</h2>
              <p class="text-body-2 grey--text mb-0">รอการอนุมัติ</p>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card class="pa-3 text-center stat-card" outlined>
              <v-icon large color="purple" class="mb-1">mdi-chart-bar</v-icon>
              <h2 class="purple--text mb-1">
                {{ formatNumber(stats.avgScore) }}
              </h2>
              <p class="text-body-2 grey--text mb-0">คะแนนเฉลี่ยรวม</p>
            </v-card>
          </v-col>
        </v-row>

        <!-- แถวที่ 2: สถิติตามระดับ -->
        <v-row class="mb-4" v-if="stats.totalAssessments > 0">
          <v-col cols="12">
            <v-card outlined class="pa-4">
              <div class="text-subtitle-2 font-weight-bold mb-3">
                <v-icon small color="primary" class="mr-1"
                  >mdi-chart-pie</v-icon
                >
                สรุปจำนวนตามระดับผลการประเมิน
              </div>
              <v-row>
                <v-col
                  cols="12"
                  sm="4"
                  md="2"
                  v-for="(grade, idx) in gradeDistribution"
                  :key="idx"
                >
                  <v-card
                    flat
                    class="text-center pa-2"
                    :style="{
                      borderLeft: '4px solid',
                      borderLeftColor: getGradeHexColor(grade.label)
                    }"
                  >
                    <div
                      class="text-h5 font-weight-bold"
                      :style="{ color: getGradeHexColor(grade.label) }"
                    >
                      {{ grade.count }}
                    </div>
                    <div class="text-caption">{{ grade.label }}</div>
                    <v-progress-linear
                      :value="
                        stats.totalAssessments > 0
                          ? (grade.count / stats.totalAssessments) * 100
                          : 0
                      "
                      :color="getGradeColor(grade.label)"
                      height="4"
                      class="mt-1"
                    ></v-progress-linear>
                    <div class="text-caption grey--text">
                      {{
                        stats.totalAssessments > 0
                          ? (
                              (grade.count / stats.totalAssessments) *
                              100
                            ).toFixed(1)
                          : 0
                      }}%
                    </div>
                  </v-card>
                </v-col>
                <!-- สถิติเพิ่มเติม: ลา / มาสาย -->
                <v-col cols="12" sm="4" md="2">
                  <v-card
                    flat
                    class="text-center pa-2"
                    style="border-left: 4px solid #FF5722"
                  >
                    <div class="text-h5 font-weight-bold deep-orange--text">
                      {{ stats.totalLateCount }}
                    </div>
                    <div class="text-caption">มาสายรวม (ครั้ง)</div>
                    <div class="text-caption grey--text mt-1">
                      ลาป่วย {{ stats.totalSickDays }} วัน
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>

        <!-- ===== Tabs หลัก ===== -->
        <v-tabs
          v-model="activeTab"
          background-color="transparent"
          color="primary"
          class="mb-2"
        >
          <v-tab key="all-assessments">
            <v-icon left small>mdi-clipboard-text</v-icon>
            ผลการประเมินทั้งหมด
          </v-tab>
          <v-tab key="by-college">
            <v-icon left small>mdi-school</v-icon>
            สรุปรายสถานศึกษา
          </v-tab>
          <v-tab key="attendance-summary">
            <v-icon left small>mdi-calendar-clock</v-icon>
            สรุปการลา/มาสาย
          </v-tab>
          <v-tab key="discipline-overview">
            <v-icon left small>mdi-gavel</v-icon>
            วินัย/คดีอาญา
          </v-tab>
          <v-tab key="files-overview">
            <v-icon left small>mdi-folder-open</v-icon>
            ไฟล์แนบทั้งหมด
          </v-tab>
          <v-tab key="audit-log">
            <v-icon left small>mdi-history</v-icon>
            ประวัติการเปลี่ยนแปลง
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="activeTab">
          <!-- ===== Tab 1: ผลการประเมินทั้งหมด ===== -->
          <v-tab-item key="all-assessments">
            <v-card flat>
              <v-card-title>
                <v-text-field
                  v-model="assessmentSearch"
                  append-icon="mdi-magnify"
                  label="ค้นหาชื่อ-สกุล, ตำแหน่ง, สถานศึกษา..."
                  single-line
                  hide-details
                  outlined
                  dense
                  class="mr-4"
                  style="max-width: 400px"
                ></v-text-field>

                <v-select
                  v-model="statusFilter"
                  :items="statusFilterOptions"
                  label="กรองสถานะ"
                  outlined
                  dense
                  clearable
                  style="max-width: 200px"
                  class="mr-4"
                ></v-select>

                <v-select
                  v-model="gradeFilter"
                  :items="gradeFilterOptions"
                  label="กรองระดับผล"
                  outlined
                  dense
                  clearable
                  style="max-width: 200px"
                ></v-select>

                <v-spacer></v-spacer>

                <v-fade-transition>
                  <v-btn
                    v-if="selectedAssessments.length > 0"
                    color="success"
                    class="mr-2"
                    @click="approveSelectedAssessments"
                    :loading="saving"
                  >
                    <v-icon left>mdi-check-all</v-icon>
                    อนุมัติที่เลือก ({{ selectedAssessments.length }})
                  </v-btn>
                </v-fade-transition>

                <v-btn-toggle v-model="viewMode" mandatory dense class="mr-2">
                  <v-btn small value="table">
                    <v-icon small>mdi-table</v-icon>
                  </v-btn>
                  <v-btn small value="card">
                    <v-icon small>mdi-view-grid</v-icon>
                  </v-btn>
                </v-btn-toggle>
              </v-card-title>

              <!-- Table View -->
              <v-data-table
                v-model="selectedAssessments"
                v-if="viewMode === 'table'"
                show-select
                :headers="allAssessmentHeaders"
                :items="filteredAssessments"
                :loading="loading"
                :search="assessmentSearch"
                class="elevation-1"
                :items-per-page="20"
                :footer-props="{ 'items-per-page-options': [10, 20, 50, 100] }"
                multi-sort
              >
                <template v-slot:item.index="{ item }">
                  {{ filteredAssessments.indexOf(item) + 1 }}
                </template>

                <template v-slot:item.full_name="{ item }">
                  <div>
                    <strong>{{ item.full_name }}</strong>
                    <div class="text-caption grey--text">
                      {{ item.id_card }}
                    </div>
                  </div>
                </template>

                <template v-slot:item.organization_name="{ item }">
                  <span class="text-caption">{{ item.organization_name }}</span>
                  <div>
                    {{ item.college_code }}
                  </div>
                </template>

                <template v-slot:item.assessment_round="{ item }">
                  <v-chip
                    x-small
                    :color="item.assessment_round === 1 ? 'blue' : 'teal'"
                    dark
                  >
                    รอบ {{ item.assessment_round }}
                  </v-chip>
                </template>

                <template v-slot:item.performance_score="{ item }">
                  <span class="font-weight-medium">{{
                    formatNumber(item.performance_score)
                  }}</span>
                  <span class="text-caption grey--text">/80</span>
                </template>

                <template v-slot:item.competency_score="{ item }">
                  <span class="font-weight-medium">{{
                    formatNumber(item.competency_score)
                  }}</span>
                  <span class="text-caption grey--text">/20</span>
                </template>

                <template v-slot:item.total_score="{ item }">
                  <v-chip small :color="getScoreColor(item.total_score)" dark>
                    {{ formatNumber(item.total_score) }}
                  </v-chip>
                </template>

                <template v-slot:item.performance_grade="{ item }">
                  <v-chip
                    small
                    :color="getGradeColor(item.performance_grade)"
                    dark
                  >
                    {{ item.performance_grade || "-" }}
                  </v-chip>
                </template>

                <template v-slot:item.assessment_status="{ item }">
                  <v-chip
                    small
                    :color="getStatusColor(item.assessment_status)"
                    dark
                  >
                    {{ getStatusLabel(item.assessment_status) }}
                  </v-chip>
                </template>

                <template v-slot:item.file_count="{ item }">
                  <v-chip
                    x-small
                    :color="item.file_count > 0 ? 'green' : 'red lighten-1'"
                    dark
                    @click="openFilePreviewDialog(item)"
                    style="cursor: pointer"
                  >
                    <v-icon left x-small>mdi-paperclip</v-icon>
                    {{ item.file_count || 0 }}
                  </v-chip>
                </template>

                <template v-slot:item.actions="{ item }">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        x-small
                        color="indigo"
                        v-bind="attrs"
                        v-on="on"
                        @click="openDetailDialog(item)"
                      >
                        <v-icon>mdi-eye</v-icon>
                      </v-btn>
                    </template>
                    <span>ดูรายละเอียด</span>
                  </v-tooltip>

                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        x-small
                        color="purple"
                        v-bind="attrs"
                        v-on="on"
                        @click="openFilePreviewDialog(item)"
                      >
                        <v-icon>mdi-folder-open</v-icon>
                      </v-btn>
                    </template>
                    <span>ดูไฟล์แนบ</span>
                  </v-tooltip>

                  <v-tooltip
                    bottom
                    v-if="item.assessment_status === 'submitted'"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        x-small
                        color="success"
                        v-bind="attrs"
                        v-on="on"
                        @click="approveAssessment(item)"
                      >
                        <v-icon>mdi-check-circle</v-icon>
                      </v-btn>
                    </template>
                    <span>อนุมัติ</span>
                  </v-tooltip>

                  <v-tooltip
                    bottom
                    v-if="item.assessment_status === 'submitted'"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        x-small
                        color="error"
                        v-bind="attrs"
                        v-on="on"
                        @click="rejectAssessment(item)"
                      >
                        <v-icon>mdi-close-circle</v-icon>
                      </v-btn>
                    </template>
                    <span>ส่งกลับแก้ไข</span>
                  </v-tooltip>
                </template>
              </v-data-table>

              <!-- Card View -->
              <v-row v-if="viewMode === 'card'" class="pa-3">
                <v-col
                  cols="12"
                  sm="6"
                  md="4"
                  lg="3"
                  v-for="(item, idx) in filteredAssessments"
                  :key="idx"
                >
                  <v-card outlined hover class="pa-3 card-hover">
                    <div class="d-flex justify-space-between align-center mb-2">
                      <v-chip
                        x-small
                        :color="item.assessment_round === 1 ? 'blue' : 'teal'"
                        dark
                      >
                        รอบ {{ item.assessment_round }}
                      </v-chip>
                      <v-chip
                        x-small
                        :color="getStatusColor(item.assessment_status)"
                        dark
                      >
                        {{ getStatusLabel(item.assessment_status) }}
                      </v-chip>
                    </div>
                    <div class="text-subtitle-2 font-weight-bold mb-1">
                      {{ item.full_name }}
                    </div>
                    <div class="text-caption grey--text mb-2">
                      {{ item.name_postion }} |
                      {{ item.college_name || item.college_code }}
                    </div>
                    <v-divider class="mb-2"></v-divider>
                    <v-row dense>
                      <v-col cols="4" class="text-center">
                        <div class="text-caption grey--text">ผลสัมฤทธิ์</div>
                        <div class="font-weight-bold">
                          {{ formatNumber(item.performance_score) }}
                        </div>
                      </v-col>
                      <v-col cols="4" class="text-center">
                        <div class="text-caption grey--text">สมรรถนะ</div>
                        <div class="font-weight-bold">
                          {{ formatNumber(item.competency_score) }}
                        </div>
                      </v-col>
                      <v-col cols="4" class="text-center">
                        <div class="text-caption grey--text">รวม</div>
                        <v-chip
                          x-small
                          :color="getScoreColor(item.total_score)"
                          dark
                        >
                          {{ formatNumber(item.total_score) }}
                        </v-chip>
                      </v-col>
                    </v-row>
                    <div class="text-center mt-2">
                      <v-chip
                        small
                        :color="getGradeColor(item.performance_grade)"
                        dark
                      >
                        {{ item.performance_grade || "-" }}
                      </v-chip>
                    </div>
                    <v-divider class="my-2"></v-divider>
                    <div class="d-flex justify-space-between">
                      <v-btn
                        x-small
                        text
                        color="indigo"
                        @click="openDetailDialog(item)"
                      >
                        <v-icon x-small left>mdi-eye</v-icon> รายละเอียด
                      </v-btn>
                      <v-btn
                        x-small
                        text
                        color="purple"
                        @click="openFilePreviewDialog(item)"
                      >
                        <v-icon x-small left>mdi-paperclip</v-icon> ไฟล์ ({{
                          item.file_count || 0
                        }})
                      </v-btn>
                      <v-btn
                        v-if="item.assessment_status === 'submitted'"
                        x-small
                        text
                        color="success"
                        @click="approveAssessment(item)"
                      >
                        <v-icon x-small left>mdi-check</v-icon> อนุมัติ
                      </v-btn>
                    </div>
                  </v-card>
                </v-col>
                <v-col cols="12" v-if="filteredAssessments.length === 0">
                  <v-alert type="info" text>ไม่พบข้อมูลการประเมิน</v-alert>
                </v-col>
              </v-row>
            </v-card>
          </v-tab-item>

          <!-- ===== Tab 2: สรุปรายสถานศึกษา ===== -->
          <v-tab-item key="by-college">
            <v-card flat>
              <v-card-title>
                <span
                  >สรุปผลการประเมินรายสถานศึกษา ปีงบประมาณ
                  {{ selectedYear }}</span
                >
              </v-card-title>

              <v-data-table
                :headers="collegeSummaryHeaders"
                :items="collegeSummaryData"
                :loading="loading"
                class="elevation-1"
                :items-per-page="20"
              >
                <template v-slot:item.index="{ item }">
                  {{ collegeSummaryData.indexOf(item) + 1 }}
                </template>

                <template v-slot:item.avg_score="{ item }">
                  <v-chip small :color="getScoreColor(item.avg_score)" dark>
                    {{ formatNumber(item.avg_score) }}
                  </v-chip>
                </template>

                <template v-slot:item.completion_rate="{ item }">
                  <div class="d-flex align-center">
                    <v-progress-linear
                      :value="item.completion_rate"
                      :color="
                        item.completion_rate >= 100
                          ? 'green'
                          : item.completion_rate >= 50
                          ? 'orange'
                          : 'red'
                      "
                      height="16"
                      rounded
                      style="max-width: 120px"
                    >
                      <span class="text-caption white--text font-weight-bold">
                        {{ item.completion_rate.toFixed(0) }}%
                      </span>
                    </v-progress-linear>
                  </div>
                </template>

                <template v-slot:item.approved_count="{ item }">
                  <v-chip x-small color="green" dark>{{
                    item.approved_count
                  }}</v-chip>
                  /
                  <v-chip x-small color="blue" dark>{{
                    item.submitted_count
                  }}</v-chip>
                  /
                  <v-chip x-small color="grey" dark>{{
                    item.draft_count
                  }}</v-chip>
                </template>

                <template v-slot:item.actions="{ item }">
                  <v-btn
                    x-small
                    text
                    color="primary"
                    @click="drillDownCollege(item.college_code)"
                  >
                    <v-icon x-small left>mdi-arrow-right</v-icon>
                    ดูรายละเอียด
                  </v-btn>
                </template>
              </v-data-table>
            </v-card>
          </v-tab-item>

          <!-- ===== Tab 3: สรุปการลา/มาสาย ===== -->
          <v-tab-item key="attendance-summary">
            <v-card flat>
              <v-card-title>
                <span
                  >สรุปข้อมูลการลา/มาสาย/ขาดราชการ ปีงบประมาณ
                  {{ selectedYear }}</span
                >
                <v-spacer></v-spacer>
                <v-text-field
                  v-model="attendanceSearch"
                  append-icon="mdi-magnify"
                  label="ค้นหา..."
                  single-line
                  hide-details
                  outlined
                  dense
                  style="max-width: 300px"
                ></v-text-field>
              </v-card-title>

              <v-data-table
                :headers="attendanceSummaryHeaders"
                :items="allAttendanceData"
                :loading="loading"
                :search="attendanceSearch"
                class="elevation-1"
                :items-per-page="20"
              >
                <template v-slot:item.index="{ item }">
                  {{ allAttendanceData.indexOf(item) + 1 }}
                </template>

                <template v-slot:item.sick_summary="{ item }">
                  <span
                    v-if="item.sick_leave_times > 0"
                    class="orange--text font-weight-medium"
                  >
                    {{ item.sick_leave_times }} ครั้ง /
                    {{ item.sick_leave_days }} วัน
                  </span>
                  <span v-else class="grey--text">-</span>
                </template>

                <template v-slot:item.personal_summary="{ item }">
                  <span
                    v-if="item.personal_leave_times > 0"
                    class="blue--text font-weight-medium"
                  >
                    {{ item.personal_leave_times }} ครั้ง /
                    {{ item.personal_leave_days }} วัน
                  </span>
                  <span v-else class="grey--text">-</span>
                </template>

                <template v-slot:item.maternity_summary="{ item }">
                  <span
                    v-if="item.maternity_leave_times > 0"
                    class="purple--text font-weight-medium"
                  >
                    {{ item.maternity_leave_times }} ครั้ง /
                    {{ item.maternity_leave_days }} วัน
                  </span>
                  <span v-else class="grey--text">-</span>
                </template>

                <template v-slot:item.military_summary="{ item }">
                  <span
                    v-if="item.military_leave_times > 0"
                    class="green--text font-weight-medium"
                  >
                    {{ item.military_leave_times }} ครั้ง /
                    {{ item.military_leave_days }} วัน
                  </span>
                  <span v-else class="grey--text">-</span>
                </template>

                <template v-slot:item.late_count="{ item }">
                  <v-chip
                    x-small
                    :color="
                      item.late_count > 3
                        ? 'red'
                        : item.late_count > 0
                        ? 'orange'
                        : 'green'
                    "
                    dark
                  >
                    {{ item.late_count || 0 }}
                  </v-chip>
                </template>

                <template v-slot:item.absent_count="{ item }">
                  <v-chip
                    x-small
                    :color="item.absent_count > 0 ? 'red darken-2' : 'green'"
                    dark
                  >
                    {{ item.absent_count || 0 }}
                  </v-chip>
                </template>
              </v-data-table>
            </v-card>
          </v-tab-item>

          <!-- ===== Tab 4: วินัย/คดีอาญา ===== -->
          <v-tab-item key="discipline-overview">
            <v-card flat>
              <v-card-title>
                <span
                  >ข้อมูลการถูกลงโทษทางวินัย/คดีอาญา ปีงบประมาณ
                  {{ selectedYear }}</span
                >
              </v-card-title>

              <v-data-table
                :headers="disciplineHeaders"
                :items="allDisciplineData"
                :loading="loading"
                class="elevation-1"
                :items-per-page="20"
              >
                <template v-slot:item.index="{ index }">
                  {{ index + 1 }}
                </template>

                <template v-slot:item.discipline_type="{ item }">
                  <v-chip
                    small
                    :color="getDisciplineTypeColor(item.discipline_type)"
                    dark
                  >
                    {{ item.discipline_type }}
                  </v-chip>
                </template>

                <template v-slot:item.court_case="{ item }">
                  <v-chip small :color="item.court_case ? 'red' : 'green'" dark>
                    {{ item.court_case ? "มีคดีอาญา" : "ไม่มี" }}
                  </v-chip>
                </template>

                <template v-slot:item.discipline_date="{ item }">
                  {{ formatDate(item.discipline_date) }}
                </template>
              </v-data-table>

              <v-alert
                v-if="allDisciplineData.length === 0 && !loading"
                type="success"
                text
                class="ma-4"
              >
                <v-icon left>mdi-check-circle</v-icon>
                ไม่พบข้อมูลการลงโทษทางวินัย/คดีอาญา ในปีงบประมาณนี้
              </v-alert>
            </v-card>
          </v-tab-item>

          <!-- ===== Tab 5: ไฟล์แนบทั้งหมด ===== -->
          <v-tab-item key="files-overview">
            <v-card flat>
              <v-card-title>
                <span
                  >ไฟล์แนบการประเมินทั้งหมด ปีงบประมาณ {{ selectedYear }}</span
                >
                <v-spacer></v-spacer>
                <v-select
                  v-model="fileCategoryFilter"
                  :items="fileCategoryFilterOptions"
                  label="กรองประเภทเอกสาร"
                  outlined
                  dense
                  clearable
                  style="max-width: 250px"
                  class="mr-2"
                ></v-select>
                <v-text-field
                  v-model="fileSearch"
                  append-icon="mdi-magnify"
                  label="ค้นหาไฟล์..."
                  single-line
                  hide-details
                  outlined
                  dense
                  style="max-width: 250px"
                ></v-text-field>
              </v-card-title>

              <!-- สรุปไฟล์ตามประเภท -->
              <v-row class="px-4 mb-3" v-if="allFilesData.length > 0">
                <v-col
                  cols="12"
                  sm="4"
                  md="2"
                  v-for="(fc, idx) in fileCategorySummary"
                  :key="idx"
                >
                  <v-card
                    flat
                    class="text-center pa-2"
                    :style="{
                      borderTop: '3px solid',
                      borderTopColor: getFileCategoryHex(fc.category)
                    }"
                  >
                    <div
                      class="text-h6 font-weight-bold"
                      :style="{ color: getFileCategoryHex(fc.category) }"
                    >
                      {{ fc.count }}
                    </div>
                    <div class="text-caption">
                      {{ getFileCategoryLabel(fc.category) }}
                    </div>
                  </v-card>
                </v-col>
              </v-row>

              <v-data-table
                :headers="allFilesHeaders"
                :items="filteredFiles"
                :loading="loadingFiles"
                :search="fileSearch"
                class="elevation-1"
                :items-per-page="20"
                dense
              >
                <template v-slot:item.index="{ index }">
                  {{ index + 1 }}
                </template>

                <template v-slot:item.file_category="{ item }">
                  <v-chip
                    x-small
                    :color="getFileCategoryColor(item.file_category)"
                    dark
                  >
                    {{ getFileCategoryLabel(item.file_category) }}
                  </v-chip>
                </template>

                <template v-slot:item.file_size="{ item }">
                  {{ formatFileSize(item.file_size) }}
                </template>

                <template v-slot:item.assessment_info="{ item }">
                  <div class="text-caption">
                    {{ item.employee_name || "-" }}
                    <span class="grey--text"
                      >(รอบ {{ item.assessment_round || "-" }})</span
                    >
                  </div>
                </template>

                <template v-slot:item.created_at="{ item }">
                  {{ formatDateTime(item.created_at) }}
                </template>

                <template v-slot:item.actions="{ item }">
                  <v-btn icon x-small color="info" @click="downloadFile(item)">
                    <v-icon>mdi-download</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    x-small
                    color="indigo"
                    @click="previewFile(item)"
                    v-if="isPreviewable(item.file_type)"
                  >
                    <v-icon>mdi-eye</v-icon>
                  </v-btn>
                </template>
              </v-data-table>
            </v-card>
          </v-tab-item>

          <!-- ===== Tab 6: ประวัติ Log ===== -->
          <v-tab-item key="audit-log">
            <v-card flat>
              <v-card-title>
                <span>ประวัติการเปลี่ยนแปลง (Audit Log)</span>
                <v-spacer></v-spacer>
                <v-select
                  v-model="logFilter"
                  :items="logFilterOptions"
                  label="ประเภท"
                  outlined
                  dense
                  style="max-width: 200px"
                  class="mr-2"
                  @change="loadLogData"
                ></v-select>
                <v-btn
                  color="primary"
                  small
                  @click="loadLogData"
                  :loading="loadingLog"
                >
                  <v-icon left>mdi-refresh</v-icon>
                  โหลดใหม่
                </v-btn>
              </v-card-title>

              <v-data-table
                :headers="logHeaders"
                :items="logData"
                :loading="loadingLog"
                class="elevation-1"
                :items-per-page="25"
                dense
              >
                <template v-slot:item.index="{ index }">
                  {{ index + 1 }}
                </template>

                <template v-slot:item.log_action="{ item }">
                  <v-chip
                    x-small
                    :color="getLogActionColor(item.log_action)"
                    dark
                  >
                    {{ getLogActionLabel(item.log_action) }}
                  </v-chip>
                </template>

                <template v-slot:item.college_code="{ item }">
                  <span class="text-caption">{{
                    getCollegeName(item.college_code) || item.college_code
                  }}</span>
                </template>

                <template v-slot:item.created_at="{ item }">
                  {{ formatDateTime(item.created_at) }}
                </template>

                <template v-slot:item.details="{ item }">
                  <v-tooltip bottom max-width="500">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon x-small color="grey" v-bind="attrs" v-on="on">
                        <v-icon>mdi-eye</v-icon>
                      </v-btn>
                    </template>
                    <div style="max-width: 480px">
                      <div v-if="item.old_data">
                        <strong>ข้อมูลเดิม:</strong>
                        {{ truncate(item.old_data, 200) }}
                      </div>
                      <div v-if="item.new_data">
                        <strong>ข้อมูลใหม่:</strong>
                        {{ truncate(item.new_data, 200) }}
                      </div>
                      <div v-if="item.action_note">
                        <strong>หมายเหตุ:</strong> {{ item.action_note }}
                      </div>
                      <div v-if="item.action_ip">
                        <strong>IP:</strong> {{ item.action_ip }}
                      </div>
                    </div>
                  </v-tooltip>
                </template>
              </v-data-table>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </base-material-card>

      <!-- ===== Dialog รายละเอียดการประเมิน ===== -->
      <v-dialog v-model="detailDialog" max-width="900px">
        <v-card v-if="selectedDetail">
          <v-card-title class="headline grey lighten-4">
            <v-icon left color="indigo">mdi-clipboard-text</v-icon>
            รายละเอียดการประเมิน - {{ selectedDetail.full_name }}
          </v-card-title>
          <v-card-text class="pt-4">
            <v-row>
              <v-col cols="12" md="6">
                <v-simple-table dense>
                  <tbody>
                    <tr>
                      <td class="font-weight-bold" width="40%">ชื่อ-สกุล</td>
                      <td>{{ selectedDetail.full_name }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">เลขบัตรประชาชน</td>
                      <td>{{ selectedDetail.id_card }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">ตำแหน่ง</td>
                      <td>{{ selectedDetail.name_postion }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">สถานศึกษา</td>
                      <td>
                        {{
                          selectedDetail.college_name ||
                            selectedDetail.college_code
                        }}
                      </td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">ปีงบประมาณ</td>
                      <td>{{ selectedDetail.assessment_year }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">รอบการประเมิน</td>
                      <td>
                        <v-chip
                          small
                          :color="
                            selectedDetail.assessment_round === 1
                              ? 'blue'
                              : 'teal'
                          "
                          dark
                        >
                          รอบที่ {{ selectedDetail.assessment_round }}
                        </v-chip>
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-col>
              <v-col cols="12" md="6">
                <v-simple-table dense>
                  <tbody>
                    <tr>
                      <td class="font-weight-bold" width="40%">
                        ผลสัมฤทธิ์ (80)
                      </td>
                      <td class="font-weight-bold text-h6">
                        {{ formatNumber(selectedDetail.performance_score) }}
                      </td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">สมรรถนะ (20)</td>
                      <td class="font-weight-bold text-h6">
                        {{ formatNumber(selectedDetail.competency_score) }}
                      </td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">คะแนนรวม</td>
                      <td>
                        <v-chip
                          :color="getScoreColor(selectedDetail.total_score)"
                          dark
                        >
                          {{ formatNumber(selectedDetail.total_score) }}
                        </v-chip>
                      </td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">สรุประดับผล</td>
                      <td>
                        <v-chip
                          :color="
                            getGradeColor(selectedDetail.performance_grade)
                          "
                          dark
                        >
                          {{ selectedDetail.performance_grade || "-" }}
                        </v-chip>
                      </td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">สถานะ</td>
                      <td>
                        <v-chip
                          small
                          :color="
                            getStatusColor(selectedDetail.assessment_status)
                          "
                          dark
                        >
                          {{ getStatusLabel(selectedDetail.assessment_status) }}
                        </v-chip>
                      </td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">ผู้ประเมิน</td>
                      <td>{{ selectedDetail.assessor_name || "-" }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">วันที่ประเมิน</td>
                      <td>{{ formatDate(selectedDetail.assessment_date) }}</td>
                    </tr>
                    <tr v-if="selectedDetail.approver_name">
                      <td class="font-weight-bold">ผู้อนุมัติ</td>
                      <td>{{ selectedDetail.approver_name }}</td>
                    </tr>
                    <tr v-if="selectedDetail.approved_date">
                      <td class="font-weight-bold">วันที่อนุมัติ</td>
                      <td>{{ formatDate(selectedDetail.approved_date) }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-col>
            </v-row>

            <v-divider class="my-3"></v-divider>

            <div class="text-subtitle-2 font-weight-bold mb-2">
              <v-icon small color="primary">mdi-note-text</v-icon> หมายเหตุ
            </div>
            <div class="text-body-2 grey--text text--darken-2">
              {{ selectedDetail.performance_note || "ไม่มีหมายเหตุ" }}
            </div>

            <v-divider class="my-3"></v-divider>

            <!-- ไฟล์แนบในรายละเอียด -->
            <div class="text-subtitle-2 font-weight-bold mb-2">
              <v-icon small color="purple">mdi-paperclip</v-icon> ไฟล์แนบ ({{
                detailFiles.length
              }})
            </div>
            <v-data-table
              :headers="detailFileHeaders"
              :items="detailFiles"
              :loading="loadingDetailFiles"
              dense
              hide-default-footer
              class="elevation-1"
              v-if="detailFiles.length > 0"
            >
              <template v-slot:item.file_category="{ item }">
                <v-chip
                  x-small
                  :color="getFileCategoryColor(item.file_category)"
                  dark
                >
                  {{ getFileCategoryLabel(item.file_category) }}
                </v-chip>
              </template>
              <template v-slot:item.file_size="{ item }">
                {{ formatFileSize(item.file_size) }}
              </template>
              <template v-slot:item.created_at="{ item }">
                {{ formatDateTime(item.created_at) }}
              </template>
              <template v-slot:item.actions="{ item }">
                <v-btn icon x-small color="info" @click="downloadFile(item)">
                  <v-icon>mdi-download</v-icon>
                </v-btn>
              </template>
            </v-data-table>
            <v-alert v-else type="warning" text dense class="mt-2">
              ยังไม่มีไฟล์แนบ
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              v-if="
                selectedDetail &&
                  selectedDetail.assessment_status === 'submitted'
              "
              color="success"
              @click="
                approveAssessment(selectedDetail);
                detailDialog = false;
              "
            >
              <v-icon left>mdi-check-circle</v-icon>
              อนุมัติ
            </v-btn>
            <v-btn
              v-if="
                selectedDetail &&
                  selectedDetail.assessment_status === 'submitted'
              "
              color="error"
              @click="
                rejectAssessment(selectedDetail);
                detailDialog = false;
              "
            >
              <v-icon left>mdi-close-circle</v-icon>
              ส่งกลับแก้ไข
            </v-btn>
            <v-btn text @click="detailDialog = false">ปิด</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- ===== Dialog ดูไฟล์แนบ ===== -->
      <v-dialog v-model="filePreviewDialog" max-width="800px">
        <v-card>
          <v-card-title class="headline grey lighten-4">
            <v-icon left color="purple">mdi-folder-open</v-icon>
            ไฟล์แนบ -
            {{ filePreviewTarget ? filePreviewTarget.full_name : "" }} (รอบ
            {{ filePreviewTarget ? filePreviewTarget.assessment_round : "" }})
          </v-card-title>
          <v-card-text class="pt-4">
            <v-data-table
              :headers="detailFileHeaders"
              :items="previewFiles"
              :loading="loadingPreviewFiles"
              dense
              class="elevation-1"
            >
              <template v-slot:item.file_category="{ item }">
                <v-chip
                  x-small
                  :color="getFileCategoryColor(item.file_category)"
                  dark
                >
                  {{ getFileCategoryLabel(item.file_category) }}
                </v-chip>
              </template>
              <template v-slot:item.file_size="{ item }">
                {{ formatFileSize(item.file_size) }}
              </template>
              <template v-slot:item.created_at="{ item }">
                {{ formatDateTime(item.created_at) }}
              </template>
              <template v-slot:item.actions="{ item }">
                <v-btn icon x-small color="info" @click="downloadFile(item)">
                  <v-icon>mdi-download</v-icon>
                </v-btn>
              </template>
            </v-data-table>
            <v-alert
              v-if="previewFiles.length === 0 && !loadingPreviewFiles"
              type="warning"
              text
              dense
              class="mt-3"
            >
              ยังไม่มีไฟล์แนบสำหรับการประเมินนี้
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="filePreviewDialog = false">ปิด</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- ===== Dialog ส่งกลับแก้ไข ===== -->
      <v-dialog v-model="rejectDialog" max-width="500px" persistent>
        <v-card>
          <v-card-title class="headline grey lighten-4">
            <v-icon left color="error">mdi-close-circle</v-icon>
            ส่งกลับแก้ไข
          </v-card-title>
          <v-card-text class="pt-4">
            <div class="mb-3">
              กำลังส่งกลับการประเมินของ
              <strong>{{ rejectTarget ? rejectTarget.full_name : "" }}</strong>
              รอบที่ {{ rejectTarget ? rejectTarget.assessment_round : "" }}
            </div>
            <v-textarea
              v-model="rejectReason"
              label="เหตุผลในการส่งกลับแก้ไข *"
              outlined
              rows="3"
              :rules="[v => !!v || 'กรุณาระบุเหตุผล']"
            ></v-textarea>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="rejectDialog = false">ยกเลิก</v-btn>
            <v-btn
              color="error"
              @click="confirmReject"
              :loading="saving"
              :disabled="!rejectReason"
            >
              <v-icon left>mdi-send</v-icon>
              ยืนยันส่งกลับ
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>


      <!-- Dialog Open view file -->
      <v-dialog v-model="viewFileDialog" max-width="800px">
        <v-card>
          <v-card-title class="headline grey lighten-4">
            <v-icon left color="purple">mdi-folder-open</v-icon>
            ดูไฟล์แนบ
          </v-card-title>
          <v-card-text class="pt-4">
            <!-- Content for viewing file iframe -->
              <iframe
                v-if="filePreviewTarget"
                :src="'https://hrvec.ovec.go.th/HRvec_api/' + filePreviewTarget.file_path"
                width="100%"
                height="600px"
                frameborder="0"
              ></iframe>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="viewFileDialog = false">ปิด</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Snackbar -->
      <v-snackbar
        v-model="snackbar.show"
        :color="snackbar.color"
        :timeout="3000"
        top
      >
        <v-icon left>{{ snackbar.icon }}</v-icon>
        {{ snackbar.text }}
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="snackbar.show = false">ปิด</v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </div>
</template>

<script>
import * as XLSX from "xlsx";

export default {
  name: "AdminAssessmentReport",
  data() {
    return {
      ApiKey: "HRvec2021",
      loading: false,
      saving: false,
      loadingFiles: false,
      loadingLog: false,
      loadingDetailFiles: false,
      loadingPreviewFiles: false,

      selectedYear: new Date().getFullYear() + 543,
      selectedRound: 0,
      selectedCollege: null,
      activeTab: 0,
      viewMode: "table",

      // ข้อมูล
      user: {},
      collegeList: [],
      allAssessments: [],
      allAttendanceData: [],
      allDisciplineData: [],
      allFilesData: [],
      logData: [],

      // ตัวกรอง
      assessmentSearch: "",
      attendanceSearch: "",
      fileSearch: "",
      statusFilter: null,
      gradeFilter: null,
      logFilter: "",
      fileCategoryFilter: null,

      // Dialog
      detailDialog: false,
      filePreviewDialog: false,
      rejectDialog: false,
      selectedDetail: null,
      detailFiles: [],
      previewFiles: [],
      filePreviewTarget: null,
      rejectTarget: null,
      rejectReason: "",
      viewFileDialog: false,
      years: [],

      roundOptions: [
        { text: "ทุกรอบ", value: 0 },
        { text: "รอบที่ 1 (1 ต.ค. - 31 มี.ค.)", value: 1 },
        { text: "รอบที่ 2 (1 เม.ย. - 30 ก.ย.)", value: 2 }
      ],

      statusFilterOptions: [
        { text: "ร่าง", value: "draft" },
        { text: "ส่งแล้ว", value: "submitted" },
        { text: "อนุมัติ", value: "approved" },
        { text: "ส่งกลับ", value: "rejected" }
      ],

      gradeFilterOptions: ["ดีเด่น", "ดีมาก", "ดี", "พอใช้", "ต้องปรับปรุง"],

      fileCategoryFilterOptions: [
        { text: "ปผ.พนร. 1-1", value: "pf_pnr_1_1" },
        { text: "ปผ.พนร. 1-2", value: "pf_pnr_1_2" },
        { text: "ปผ.พนร. 1-3", value: "pf_pnr_1_3" },
        { text: "ปผ.พนร. 1-4", value: "pf_pnr_1_4" },
        { text: "เอกสารประกอบ", value: "supporting_doc" },
        { text: "อื่นๆ", value: "other" }
      ],

      logFilterOptions: [
        { text: "ทั้งหมด", value: "" },
        { text: "เพิ่มใหม่", value: "insert" },
        { text: "แก้ไข", value: "update" },
        { text: "ลบ", value: "delete" },
        { text: "เปลี่ยนสถานะ", value: "status_change" },
        { text: "อัปโหลดไฟล์", value: "file_upload" },
        { text: "ลบไฟล์", value: "file_delete" }
      ],

      // Headers
      allAssessmentHeaders: [
        { text: "#", value: "index", width: "50px", sortable: false },
        { text: "ชื่อ-สกุล", value: "full_name", width: "180px" },
        { text: "สถานศึกษา", value: "organization_name", width: "150px" },
        { text: "ตำแหน่ง", value: "name_postion", width: "120px" },
        {
          text: "รอบ",
          value: "assessment_round",
          width: "80px",
          align: "center"
        },
        {
          text: "ผลสัมฤทธิ์",
          value: "performance_score",
          width: "100px",
          align: "center"
        },
        {
          text: "สมรรถนะ",
          value: "competency_score",
          width: "90px",
          align: "center"
        },
        { text: "รวม", value: "total_score", width: "80px", align: "center" },
        {
          text: "ระดับผล",
          value: "performance_grade",
          width: "110px",
          align: "center"
        },
        {
          text: "สถานะ",
          value: "assessment_status",
          width: "100px",
          align: "center"
        },
        { text: "ไฟล์", value: "file_count", width: "60px", align: "center" },
        {
          text: "ดำเนินการ",
          value: "actions",
          width: "140px",
          align: "center",
          sortable: false
        }
      ],

      collegeSummaryHeaders: [
        { text: "#", value: "index", width: "50px", sortable: false },
        { text: "สถานศึกษา", value: "organization_name", width: "200px" },
        { text: "รหัส", value: "college_code", width: "80px" },
        {
          text: "จำนวนพนักงาน",
          value: "employee_count",
          width: "120px",
          align: "center"
        },
        {
          text: "จำนวนประเมิน",
          value: "assessment_count",
          width: "120px",
          align: "center"
        },
        {
          text: "คะแนนเฉลี่ย",
          value: "avg_score",
          width: "110px",
          align: "center"
        },
        {
          text: "ความครบถ้วน",
          value: "completion_rate",
          width: "150px",
          align: "center"
        },
        {
          text: "อนุมัติ/ส่ง/ร่าง",
          value: "approved_count",
          width: "140px",
          align: "center"
        },
        { text: "", value: "actions", width: "120px", sortable: false }
      ],

      attendanceSummaryHeaders: [
        { text: "#", value: "index", width: "50px", sortable: false },
        { text: "ชื่อ-สกุล", value: "full_name", width: "180px" },
        { text: "สถานศึกษา", value: "organization_name", width: "150px" },
        {
          text: "ลาป่วย",
          value: "sick_summary",
          width: "130px",
          align: "center",
          sortable: false
        },
        {
          text: "ลากิจส่วนตัว",
          value: "personal_summary",
          width: "130px",
          align: "center",
          sortable: false
        },
        {
          text: "ลาคลอดบุตร",
          value: "maternity_summary",
          width: "130px",
          align: "center",
          sortable: false
        },
        {
          text: "ลาราชการทหาร",
          value: "military_summary",
          width: "130px",
          align: "center",
          sortable: false
        },
        { text: "มาสาย", value: "late_count", width: "80px", align: "center" },
        {
          text: "ขาดราชการ",
          value: "absent_count",
          width: "90px",
          align: "center"
        }
      ],

      disciplineHeaders: [
        { text: "#", value: "index", width: "50px", sortable: false },
        { text: "ชื่อ-สกุล", value: "full_name", width: "180px" },
        { text: "สถานศึกษา", value: "college_name", width: "150px" },
        { text: "ประเภท", value: "discipline_type", width: "130px" },
        { text: "วันที่", value: "discipline_date", width: "110px" },
        {
          text: "คดีอาญา",
          value: "court_case",
          width: "110px",
          align: "center"
        },
        { text: "เหตุผล", value: "discipline_reason", width: "250px" }
      ],

      allFilesHeaders: [
        { text: "#", value: "index", width: "40px", sortable: false },
        { text: "ประเภท", value: "file_category", width: "120px" },
        { text: "ชื่อไฟล์", value: "file_name", width: "200px" },
        { text: "ขนาด", value: "file_size", width: "70px" },
        { text: "การประเมิน", value: "assessment_info", width: "180px" },
        { text: "สถานศึกษา", value: "college_name", width: "130px" },
        { text: "ผู้อัปโหลด", value: "uploaded_by_name", width: "110px" },
        { text: "วันที่", value: "created_at", width: "130px" },
        { text: "", value: "actions", width: "70px", sortable: false }
      ],

      logHeaders: [
        { text: "#", value: "index", width: "40px", sortable: false },
        { text: "ประเภท", value: "log_action", width: "110px" },
        { text: "สถานศึกษา", value: "college_code", width: "140px" },
        { text: "เลขบัตร", value: "id_card", width: "120px" },
        {
          text: "รอบ",
          value: "assessment_round",
          width: "50px",
          align: "center"
        },
        { text: "ฟิลด์ที่เปลี่ยน", value: "changed_fields", width: "150px" },
        { text: "ผู้ดำเนินการ", value: "action_by_name", width: "110px" },
        { text: "หมายเหตุ", value: "action_note", width: "180px" },
        { text: "วันเวลา", value: "created_at", width: "140px" },
        { text: "", value: "details", width: "40px", sortable: false }
      ],

      detailFileHeaders: [
        { text: "ประเภท", value: "file_category", width: "130px" },
        { text: "ชื่อไฟล์", value: "file_name", width: "200px" },
        { text: "ขนาด", value: "file_size", width: "80px" },
        { text: "ผู้อัปโหลด", value: "uploaded_by_name", width: "110px" },
        { text: "วันที่", value: "created_at", width: "130px" },
        { text: "", value: "actions", width: "60px", sortable: false }
      ],

      snackbar: { show: false, color: "success", icon: "mdi-check", text: "" },
      selectedAssessments: [] // สำหรับเก็บรายการที่ถูก Tick checkbox
    };
  },

  computed: {
    stats() {
      const data = this.allAssessments;
      const att = this.allAttendanceData;
      const totalEmployees = new Set(data.map(d => d.id_card)).size;
      const pendingApproval = data.filter(
        d => d.assessment_status === "submitted"
      ).length;
      const scores = data
        .filter(d => d.total_score > 0)
        .map(d => parseFloat(d.total_score));
      const avgScore =
        scores.length > 0
          ? scores.reduce((a, b) => a + b, 0) / scores.length
          : 0;
      const totalLateCount = att.reduce(
        (sum, a) => sum + (parseInt(a.late_count) || 0),
        0
      );
      const totalSickDays = att.reduce(
        (sum, a) => sum + (parseFloat(a.sick_leave_days) || 0),
        0
      );

      return {
        totalEmployees,
        totalAssessments: data.length,
        pendingApproval,
        avgScore,
        totalLateCount,
        totalSickDays
      };
    },

    gradeDistribution() {
      const grades = ["ดีเด่น", "ดีมาก", "ดี", "พอใช้", "ต้องปรับปรุง"];
      return grades.map(g => ({
        label: g,
        count: this.allAssessments.filter(a => a.performance_grade === g).length
      }));
    },

    filteredAssessments() {
      let data = [...this.allAssessments];
      if (this.statusFilter) {
        data = data.filter(d => d.assessment_status === this.statusFilter);
      }
      if (this.gradeFilter) {
        data = data.filter(d => d.performance_grade === this.gradeFilter);
      }
      return data;
    },

    collegeSummaryData() {
      const map = {};
      this.allAssessments.forEach(a => {
        const key = a.college_code;
        if (!map[key]) {
          map[key] = {
            college_code: key,
            organization_name: a.organization_name || key,
            employee_count: 0,
            assessment_count: 0,
            total_score_sum: 0,
            approved_count: 0,
            submitted_count: 0,
            draft_count: 0,
            employees: new Set()
          };
        }
        map[key].assessment_count++;
        map[key].employees.add(a.id_card);
        map[key].total_score_sum += parseFloat(a.total_score) || 0;
        if (a.assessment_status === "approved") map[key].approved_count++;
        else if (a.assessment_status === "submitted")
          map[key].submitted_count++;
        else map[key].draft_count++;
      });

      return Object.values(map).map(c => ({
        ...c,
        employee_count: c.employees.size,
        avg_score:
          c.assessment_count > 0 ? c.total_score_sum / c.assessment_count : 0,
        completion_rate:
          c.employees.size > 0
            ? (c.assessment_count / (c.employees.size * 2)) * 100
            : 0
      }));
    },

    filteredFiles() {
      let data = [...this.allFilesData];
      if (this.fileCategoryFilter) {
        data = data.filter(f => f.file_category === this.fileCategoryFilter);
      }
      return data;
    },

    fileCategorySummary() {
      const map = {};
      this.allFilesData.forEach(f => {
        const cat = f.file_category || "other";
        if (!map[cat]) map[cat] = { category: cat, count: 0 };
        map[cat].count++;
      });
      return Object.values(map).sort((a, b) => b.count - a.count);
    }
  },

  async mounted() {
    await this.initializeComponent();
  },

  methods: {
    async approveSelectedAssessments() {
      // กรองเฉพาะรายการที่เป็นสถานะ 'submitted' เท่านั้น (ป้องกันการอนุมัติซ้ำหรือรายการที่เป็นร่าง)
      const targets = this.selectedAssessments.filter(
        item => item.assessment_status === "submitted"
      );

      if (targets.length === 0) {
        this.showSnackbar(
          "ไม่มีรายการที่สามารถอนุมัติได้ (ต้องมีสถานะ 'ส่งแล้ว')",
          "warning",
          "mdi-alert"
        );
        return;
      }

      if (
        !confirm(
          `คุณต้องการอนุมัติการประเมินทั้งหมด ${targets.length} รายการที่เลือกใช่หรือไม่?`
        )
      )
        return;

      this.saving = true;
      try {
        let successCount = 0;

        // วนลูปส่ง API (ในกรณี API เดิมรับทีละ ID)
        for (const item of targets) {
          let result = await this.$http.post("gov_em_assessment.php", {
            ApiKey: this.ApiKey,
            action: "change_status",
            id: item.id,
            assessment_status: "approved",
            action_by: this.user.id_card || this.user.user_ID,
            action_by_name: this.user.user_firstname || "",
            approver_id_card: this.user.id_card || "",
            approver_name: this.user.user_firstname || ""
          });
          if (result.data.status) successCount++;
        }

        this.showSnackbar(
          `อนุมัติสำเร็จ ${successCount} รายการ`,
          "success",
          "mdi-check-circle"
        );
        this.selectedAssessments = []; // ล้างค่าที่เลือก
        await this.loadAssessments(); // โหลดข้อมูลใหม่
      } catch (error) {
        this.showSnackbar("เกิดข้อผิดพลาดบางประการ", "error", "mdi-alert");
      } finally {
        this.saving = false;
      }
    },
    async initializeComponent() {
      this.loading = true;
      try {
        let userSession = JSON.parse(sessionStorage.getItem("user")) || {};
        let resultUser = await this.$http.post("user.php", {
          ApiKey: this.ApiKey,
          user_ID: userSession.user_ID
        });
        this.user = resultUser.data;
        this.generateYears();
        await this.loadCollegeList();
        await this.loadAllData();
      } catch (error) {
        this.showSnackbar(
          "เกิดข้อผิดพลาดในการโหลดข้อมูล",
          "error",
          "mdi-alert"
        );
      } finally {
        this.loading = false;
      }
    },

    generateYears() {
      const currentYear = new Date().getFullYear() + 543;
      this.years = [];
      for (let i = 0; i < 5; i++) {
        this.years.push(currentYear - i);
      }
    },

    async loadCollegeList() {
      try {
        let result = await this.$http.post("user.php", {
          ApiKey: this.ApiKey,
          show_user_gov: "ok"
        });
        this.collegeList = result.data || [];
      } catch (error) {
        this.collegeList = [];
      }
    },

    getCollegeName(code) {
      const c = this.collegeList.find(cl => cl.college_code === code);
      return c ? c.college_name : code;
    },

    async loadAllData() {
      this.loading = true;
      try {
        await Promise.all([
          this.loadAssessments(),
          this.loadAttendance(),
          this.loadDiscipline(),
          this.loadAllFiles()
        ]);
      } catch (error) {
        this.showSnackbar(
          "เกิดข้อผิดพลาดในการโหลดข้อมูล",
          "error",
          "mdi-alert"
        );
      } finally {
        this.loading = false;
      }
    },

    async loadAssessments() {
      try {
        let payload = {
          ApiKey: this.ApiKey,
          action: "get",
          assessment_year: this.selectedYear
        };
        if (this.selectedRound > 0)
          payload.assessment_round = this.selectedRound;
        if (this.selectedCollege) payload.college_code = this.selectedCollege;

        let result = await this.$http.post("gov_em_assessment.php", payload);
        let data = result.data || [];

        // เสริมชื่อสถานศึกษา
        this.allAssessments = data.map(item => ({
          ...item,
          college_name: this.getCollegeName(item.college_code)
        }));
      } catch (error) {
        this.allAssessments = [];
      }
    },

    async loadAttendance() {
      try {
        let payload = {
          ApiKey: this.ApiKey,
          action: "get",
          attendance_year: this.selectedYear
        };
        if (this.selectedCollege) payload.college_code = this.selectedCollege;

        let result = await this.$http.post("gov_em_attendance.php", payload);
        this.allAttendanceData = (result.data || []).map(item => ({
          ...item,
          college_name: this.getCollegeName(item.college_code)
        }));
      } catch (error) {
        this.allAttendanceData = [];
      }
    },

    async loadDiscipline() {
      try {
        let payload = {
          ApiKey: this.ApiKey,
          action: "get",
          discipline_year: this.selectedYear
        };
        if (this.selectedCollege) payload.college_code = this.selectedCollege;

        let result = await this.$http.post("gov_em_discipline.php", payload);
        this.allDisciplineData = (result.data || []).map(item => ({
          ...item,
          college_name: this.getCollegeName(item.college_code)
        }));
      } catch (error) {
        this.allDisciplineData = [];
      }
    },

    async loadAllFiles() {
      this.loadingFiles = true;
      try {
        let payload = {
          ApiKey: this.ApiKey,
          action: "get_all_files",
          assessment_year: this.selectedYear
        };
        if (this.selectedCollege) payload.college_code = this.selectedCollege;

        let result = await this.$http.post("gov_em_assessment.php", payload);
        this.allFilesData = (result.data || []).map(item => ({
          ...item,
          college_name: this.getCollegeName(item.college_code)
        }));
      } catch (error) {
        this.allFilesData = [];
      } finally {
        this.loadingFiles = false;
      }
    },

    async loadLogData() {
      this.loadingLog = true;
      try {
        let payload = {
          ApiKey: this.ApiKey,
          action: "get_log",
          assessment_year: this.selectedYear,
          limit: 200
        };
        if (this.selectedCollege) payload.college_code = this.selectedCollege;
        if (this.logFilter) payload.log_action = this.logFilter;

        let result = await this.$http.post("gov_em_assessment.php", payload);
        this.logData = result.data || [];
      } catch (error) {
        this.logData = [];
      } finally {
        this.loadingLog = false;
      }
    },

    // === Dialog Methods ===
    async openDetailDialog(item) {
      this.selectedDetail = item;
      this.detailDialog = true;
      this.loadingDetailFiles = true;
      try {
        let result = await this.$http.post("gov_em_assessment.php", {
          ApiKey: this.ApiKey,
          action: "get_files",
          assessment_id: item.id
        });
        this.detailFiles = result.data || [];
      } catch (error) {
        this.detailFiles = [];
      } finally {
        this.loadingDetailFiles = false;
      }
    },

    async openFilePreviewDialog(item) {
      this.filePreviewTarget = item;
      this.filePreviewDialog = true;
      this.loadingPreviewFiles = true;
      try {
        let result = await this.$http.post("gov_em_assessment.php", {
          ApiKey: this.ApiKey,
          action: "get_files",
          assessment_id: item.id
        });
        this.previewFiles = result.data || [];
      } catch (error) {
        this.previewFiles = [];
      } finally {
        this.loadingPreviewFiles = false;
      }
    },

    // === อนุมัติ / ส่งกลับ ===
    async approveAssessment(item) {
      if (
        !confirm(
          `ต้องการอนุมัติการประเมินของ ${item.full_name} รอบที่ ${item.assessment_round} หรือไม่?`
        )
      )
        return;

      this.saving = true;
      try {
        let result = await this.$http.post("gov_em_assessment.php", {
          ApiKey: this.ApiKey,
          action: "change_status",
          id: item.id,
          assessment_status: "approved",
          action_by: this.user.id_card || this.user.user_ID,
          action_by_name: this.user.user_firstname || "",
          approver_id_card: this.user.id_card || "",
          approver_name: this.user.user_firstname || ""
        });
        if (result.data.status) {
          this.showSnackbar(
            "อนุมัติการประเมินเรียบร้อย",
            "success",
            "mdi-check-circle"
          );
          await this.loadAssessments();
        } else {
          this.showSnackbar(
            result.data.message || "เกิดข้อผิดพลาด",
            "error",
            "mdi-alert"
          );
        }
      } catch (error) {
        this.showSnackbar("เกิดข้อผิดพลาด", "error", "mdi-alert");
      } finally {
        this.saving = false;
      }
    },

    rejectAssessment(item) {
      this.rejectTarget = item;
      this.rejectReason = "";
      this.rejectDialog = true;
    },

    async confirmReject() {
      if (!this.rejectReason || !this.rejectTarget) return;

      this.saving = true;
      try {
        let result = await this.$http.post("gov_em_assessment.php", {
          ApiKey: this.ApiKey,
          action: "change_status",
          id: this.rejectTarget.id,
          assessment_status: "rejected",
          action_by: this.user.id_card || this.user.user_ID,
          action_by_name: this.user.user_firstname || "",
          action_note: this.rejectReason
        });
        if (result.data.status) {
          this.showSnackbar("ส่งกลับแก้ไขเรียบร้อย", "success", "mdi-check");
          this.rejectDialog = false;
          await this.loadAssessments();
        } else {
          this.showSnackbar(
            result.data.message || "เกิดข้อผิดพลาด",
            "error",
            "mdi-alert"
          );
        }
      } catch (error) {
        this.showSnackbar("เกิดข้อผิดพลาด", "error", "mdi-alert");
      } finally {
        this.saving = false;
      }
    },

    drillDownCollege(collegeCode) {
      this.selectedCollege = collegeCode;
      this.activeTab = 0;
      this.loadAllData();
    },

    downloadFile(file) {
      this.filePreviewTarget = file;
      this.viewFileDialog = true;
    },

    isPreviewable(fileType) {
      return (
        fileType && (fileType.includes("pdf") || fileType.includes("image"))
      );
    },

    previewFile(file) {
      window.open("/HRvec_api/" + file.file_path, "_blank");
    },

    // === ส่งออก Excel ===
    exportAdminReport() {
      const wb = XLSX.utils.book_new();

      // Sheet 1: ผลการประเมิน
      const assessRows = this.allAssessments.map((a, i) => [
        i + 1,
        a.full_name,
        a.college_name || a.college_code,
        a.name_postion,
        `รอบ ${a.assessment_round}`,
        a.performance_score,
        a.competency_score,
        a.total_score,
        a.performance_grade,
        this.getStatusLabel(a.assessment_status),
        a.file_count || 0,
        a.assessor_name || "-",
        a.assessment_date || "-"
      ]);

      const assessSheet = XLSX.utils.aoa_to_sheet([
        [
          `รายงานผลการประเมินการปฏิบัติงาน (Admin) ปีงบประมาณ ${this.selectedYear}`
        ],
        [
          `เกณฑ์คะแนน: ดีเด่น 95-100 | ดีมาก 85-94 | ดี 75-84 | พอใช้ 65-74 | ต้องปรับปรุง <65 (ตามประกาศ สอศ. พ.ศ. 2569)`
        ],
        [],
        [
          "#",
          "ชื่อ-สกุล",
          "สถานศึกษา",
          "ตำแหน่ง",
          "รอบ",
          "ผลสัมฤทธิ์(80)",
          "สมรรถนะ(20)",
          "รวม",
          "ระดับผล",
          "สถานะ",
          "ไฟล์",
          "ผู้ประเมิน",
          "วันที่ประเมิน"
        ],
        ...assessRows
      ]);
      assessSheet["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 12 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: 12 } }
      ];
      assessSheet["!cols"] = [
        { wch: 5 },
        { wch: 22 },
        { wch: 20 },
        { wch: 15 },
        { wch: 10 },
        { wch: 10 },
        { wch: 10 },
        { wch: 8 },
        { wch: 12 },
        { wch: 10 },
        { wch: 6 },
        { wch: 15 },
        { wch: 12 }
      ];
      XLSX.utils.book_append_sheet(wb, assessSheet, "ผลการประเมิน");

      // Sheet 2: การลา
      const attRows = this.allAttendanceData.map((a, i) => [
        i + 1,
        a.full_name,
        a.college_name || a.college_code,
        a.sick_leave_times || 0,
        a.sick_leave_days || 0,
        a.personal_leave_times || 0,
        a.personal_leave_days || 0,
        a.maternity_leave_times || 0,
        a.maternity_leave_days || 0,
        a.military_leave_times || 0,
        a.military_leave_days || 0,
        a.late_count || 0,
        a.absent_count || 0
      ]);

      const attSheet = XLSX.utils.aoa_to_sheet([
        [`สรุปการลา/มาสาย/ขาดราชการ ปีงบประมาณ ${this.selectedYear}`],
        [],
        [
          "#",
          "ชื่อ-สกุล",
          "สถานศึกษา",
          "ลาป่วย(ครั้ง)",
          "ลาป่วย(วัน)",
          "ลากิจ(ครั้ง)",
          "ลากิจ(วัน)",
          "ลาคลอด(ครั้ง)",
          "ลาคลอด(วัน)",
          "ลาทหาร(ครั้ง)",
          "ลาทหาร(วัน)",
          "มาสาย",
          "ขาดราชการ"
        ],
        ...attRows
      ]);
      attSheet["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 12 } }];
      XLSX.utils.book_append_sheet(wb, attSheet, "การลา-มาสาย");

      const fileName = `Admin_ปผ_พนร_${this.selectedYear}.xlsx`;
      XLSX.writeFile(wb, fileName);
      this.showSnackbar("ส่งออก Excel เรียบร้อย", "success", "mdi-download");
    },

    // === Helper Functions ===
    getScoreColor(score) {
      if (score >= 95) return "green";
      if (score >= 85) return "blue";
      if (score >= 75) return "teal";
      if (score >= 65) return "orange";
      return "red";
    },

    getGradeColor(grade) {
      const map = {
        ดีเด่น: "green",
        ดีมาก: "blue",
        ดี: "teal",
        พอใช้: "orange",
        ต้องปรับปรุง: "red"
      };
      return map[grade] || "grey";
    },

    getGradeHexColor(grade) {
      const map = {
        ดีเด่น: "#4CAF50",
        ดีมาก: "#2196F3",
        ดี: "#009688",
        พอใช้: "#FF9800",
        ต้องปรับปรุง: "#F44336"
      };
      return map[grade] || "#9E9E9E";
    },

    getStatusColor(status) {
      const map = {
        draft: "grey",
        submitted: "blue",
        approved: "green",
        rejected: "red"
      };
      return map[status] || "grey";
    },

    getStatusLabel(status) {
      const map = {
        draft: "ร่าง",
        submitted: "ส่งแล้ว",
        approved: "อนุมัติ",
        rejected: "ส่งกลับ"
      };
      return map[status] || status || "ร่าง";
    },

    getDisciplineTypeColor(type) {
      const map = {
        ตักเตือน: "yellow darken-2",
        ภาคทัณฑ์: "orange",
        ตัดเงินเดือน: "deep-orange",
        ลดเงินเดือน: "red",
        ปลดออก: "red darken-2",
        ไล่ออก: "red darken-4"
      };
      return map[type] || "grey";
    },

    getLogActionColor(action) {
      const map = {
        insert: "green",
        update: "blue",
        delete: "red",
        status_change: "purple",
        file_upload: "teal",
        file_delete: "orange"
      };
      return map[action] || "grey";
    },

    getLogActionLabel(action) {
      const map = {
        insert: "เพิ่มใหม่",
        update: "แก้ไข",
        delete: "ลบ",
        status_change: "เปลี่ยนสถานะ",
        file_upload: "อัปโหลดไฟล์",
        file_delete: "ลบไฟล์"
      };
      return map[action] || action;
    },

    getFileCategoryColor(cat) {
      const map = {
        pf_pnr_1_1: "blue",
        pf_pnr_1_2: "teal",
        pf_pnr_1_3: "purple",
        pf_pnr_1_4: "indigo",
        supporting_doc: "orange",
        other: "grey"
      };
      return map[cat] || "grey";
    },

    getFileCategoryLabel(cat) {
      const map = {
        pf_pnr_1_1: "ปผ.พนร. 1-1",
        pf_pnr_1_2: "ปผ.พนร. 1-2",
        pf_pnr_1_3: "ปผ.พนร. 1-3",
        pf_pnr_1_4: "ปผ.พนร. 1-4",
        supporting_doc: "เอกสารประกอบ",
        other: "อื่นๆ"
      };
      return map[cat] || cat;
    },

    getFileCategoryHex(cat) {
      const map = {
        pf_pnr_1_1: "#2196F3",
        pf_pnr_1_2: "#009688",
        pf_pnr_1_3: "#9C27B0",
        pf_pnr_1_4: "#3F51B5",
        supporting_doc: "#FF9800",
        other: "#9E9E9E"
      };
      return map[cat] || "#9E9E9E";
    },

    formatNumber(value) {
      if (isNaN(value) || value === null || value === undefined) return "0.00";
      return Number(value).toFixed(2);
    },

    formatDate(date) {
      if (!date) return "";
      return new Date(date).toLocaleDateString("th-TH");
    },

    formatDateTime(dt) {
      if (!dt) return "";
      return new Date(dt).toLocaleString("th-TH", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    },

    formatFileSize(bytes) {
      if (!bytes) return "0 B";
      const k = 1024;
      const sizes = ["B", "KB", "MB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
    },

    truncate(text, maxLen) {
      if (!text) return "";
      if (typeof text === "object") text = JSON.stringify(text);
      return text.length > maxLen ? text.substring(0, maxLen) + "..." : text;
    },

    showSnackbar(text, color = "success", icon = "mdi-check") {
      this.snackbar = { show: true, text, color, icon };
    }
  }
};
</script>

<style scoped>
.stat-card {
  transition: transform 0.2s, box-shadow 0.2s;
}
.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12) !important;
}

.card-hover {
  transition: transform 0.2s, box-shadow 0.2s;
}
.card-hover:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15) !important;
}

.v-data-table >>> .v-data-table__wrapper {
  overflow-x: auto;
  max-height: 600px;
  overflow-y: auto;
}

.v-tabs >>> .v-tab {
  text-transform: none;
  font-weight: 500;
}

h2 {
  font-size: 1.75rem;
  font-weight: 700;
}
</style>
